import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Equipment } from '@models/equipment.model';
import { PagedResponse } from '@models/response.model';

import { TableConfig } from '@app/shared/util/table-config.utils';
import { AppUtils } from '@app/shared/util/app.utils';

import { environment } from 'environments/environment';

import * as _ from 'lodash';
import { PartNumber } from "@app/shared/models/partNumber.model";
import { Line } from "@models/line.model";
import { MmdmIntegrationStatusResult } from "@models/mmdmIntegrationStatusResult.model";
import { map } from "rxjs/operators";
import { PartNumberEquivalent } from "@models/partNumberEquivalent.model";

/**
 * Service class for handling CRUD operations on {@link Equipment} objects.
 */
@Injectable()
export class PartNumbersService {

    static readonly PART_NUMBER_API_GET_PATH = environment.baseURL + '/api/part';
    static readonly PART_NUMBER_API_REPORT_DOWNLOAD_PATH = environment.baseURL + '/api/part/download';


    constructor(private http: HttpClient) {
    }

    getAllPartNumbers(tableConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(tableConfig);
        return this.http.get<PagedResponse<PartNumber>>(PartNumbersService.PART_NUMBER_API_GET_PATH, {params: params})
            .pipe(map(this.mapPartNumbers));
    }

    getPartNumbersForLine(lineId: number) {
        let params = new HttpParams();
        if (lineId) {
            params = params.set('lineId', lineId.toString());
        }
        return this.http.get<PagedResponse<PartNumber>>(PartNumbersService.PART_NUMBER_API_GET_PATH, {params: params})
            .pipe(map(this.mapPartNumbers));
    }

    getPartNumberById(id: number) {
        return this.http.get<PartNumber>(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/${id}`)
            .pipe(map(partNumber => new PartNumber(partNumber)));
    }

    getPartNumbersByLine(line: Line) {
        let params = new HttpParams();
        params = params.set('lineId', line.id.toString());
        return this.http.get<PagedResponse<any>>(PartNumbersService.PART_NUMBER_API_GET_PATH, {params: params})
            .pipe(map(this.mapResponse));
    }

    syncPartNumbers() {
        return this.http.post(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/mmdm/sync`, {type: 1});
    }

    getStatusForSyncPartNumbers() {
        return this.http.get<MmdmIntegrationStatusResult>(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/mmdm/status`);
    }

    getAffectedToolsForUpdate(id: number, toolIds: number[]) {
        let params = new HttpParams();
        for (const item of toolIds) {
            params = params.append('toolIds', item.toString());
        }
        return this.http.get(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/${id}/affectedTools`, {params: params});
    }

    private mapResponse = res => {
        return {
            items: res.items.map(item => new PartNumber(item)),
            total: res.total
        };
    }

    mapPartNumbers = res => {
        return {
            partNumbers: res.items.map(partNumber => new PartNumber(partNumber)),
            count: res.total
        };
    };

    createPartNumber(partNumber) {
        return this.http.post<PartNumber>(PartNumbersService.PART_NUMBER_API_GET_PATH, this.getCopyPartNumber(partNumber));
    }

    updatePartNumber(partNumber) {
        const copyPartNumber = this.getCopyPartNumber(partNumber);
        return this.http.put<PartNumber>(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/${copyPartNumber.id}`, copyPartNumber);
    }

    updateStrainEquivalentPartNumbers(partNumberId: number, partNumberIds: number[]) {
        return this.http.post<PartNumberEquivalent[]>(
            `${PartNumbersService.PART_NUMBER_API_GET_PATH}/${partNumberId}/strain-equivalent-part-numbers`,
            {partNumberIds: partNumberIds || []}
        );
    }

    deletePartNumber(partNumber) {
        return this.http.delete<PartNumber>(`${PartNumbersService.PART_NUMBER_API_GET_PATH}/${partNumber.id}`);
    }

    getCopyPartNumber(partNumber) {
        const copyPartNumber: any = {
            id: partNumber.id,
            lines: _.map(partNumber.lines, 'id'),
            equipment: _.map(partNumber.equipment, 'id'),
            insertionCycles: _.map(partNumber.insertionCycles, 'id'),
            name: partNumber.name,
            description: partNumber.description,
            sapFamily: partNumber.sapFamily,
            sapGroup: partNumber.sapGroup,
            type: partNumber.type,
            ecam: partNumber.ecam,
            ecamVersion: partNumber.ecamVersion,
            bomStatus: partNumber.bomStatus,
            toolIds: partNumber.toolIds,
            valueStream: partNumber.valueStream,
            equivalentPartNumberIds: _.map(partNumber.equivalentPartNumbers, 'id'),
        };
        return copyPartNumber;
    }

    downloadPartNumberReport(requestConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(requestConfig);
        return this.http.get(PartNumbersService.PART_NUMBER_API_REPORT_DOWNLOAD_PATH,
            {observe: 'response', params: params, responseType: 'blob'});
    }
}
